"use client";

import { useCallback, useEffect, useRef, useState } from "react";

import { concernsSummary, copyFor, planHeadline } from "@/lib/onboarding/copy";
import { cn } from "@/lib/utils";

import type { StepProps } from "../types";
import { ContinueButton, StepFooter, StepHeader } from "../ui";

/** How long a pointer must hold the commit button. */
const HOLD_MS = 1000;

/** The personalized plan: the profession's four phases as a timeline, then a
 *  press-and-hold commitment. Phase words come from `copyFor(track)` data, so
 *  nothing here branches on profession. */
export function PlanStep({ state, advance }: StepProps) {
  const { track, exam_timeframe, concerns } = state.answers;
  const commit = useHoldToCommit(advance);

  if (!track) {
    return (
      <>
        <StepHeader title="Your plan." />
        <StepFooter>
          <ContinueButton onClick={() => advance()}>Continue</ContinueButton>
        </StepFooter>
      </>
    );
  }

  const phases = copyFor(track).plan;
  const focus = concernsSummary(concerns);

  return (
    <>
      <StepHeader kicker="Your plan" title={planHeadline(exam_timeframe || undefined, track)} />
      <ol className="mt-7">
        {phases.map((phase, i) => {
          const last = i === phases.length - 1;
          return (
            <li key={phase.title} className="flex gap-4">
              <div aria-hidden="true" className="flex flex-col items-center pt-1">
                <span
                  className={cn(
                    "size-3 shrink-0 rounded-full ring-2 ring-primary",
                    i === 0 ? "bg-primary" : "bg-muted",
                  )}
                />
                {last ? null : <span className="mt-1.5 w-0.5 flex-1 bg-border" />}
              </div>
              <div className={cn("min-w-0 flex-1", last ? null : "pb-5")}>
                <p className="font-mono text-xs text-primary">STEP {i + 1}</p>
                <p className="text-base font-semibold text-foreground">
                  {phase.title}
                </p>
                <p className="text-sm text-muted-foreground">{phase.detail}</p>
              </div>
            </li>
          );
        })}
      </ol>
      {focus ? (
        <p className="mt-5 text-sm text-muted-foreground">Focus: {focus}</p>
      ) : null}
      <StepFooter>
        <button
          type="button"
          aria-label="Press and hold to commit to this plan"
          onPointerDown={(e) => {
            // Primary button / touch / pen only; a right-click must not start a hold.
            if (e.button === 0) commit.start();
          }}
          onPointerUp={commit.cancel}
          onPointerLeave={commit.cancel}
          onPointerCancel={commit.cancel}
          onContextMenu={(e) => e.preventDefault()}
          onClick={(e) => {
            // Keyboard / assistive-tech activation has no pointer: commit
            // immediately rather than demanding a hold.
            if (e.detail === 0) commit.now();
          }}
          className="relative h-14 w-full touch-manipulation md:mx-auto md:max-w-md overflow-hidden rounded-xl border border-primary bg-card outline-none select-none [-webkit-touch-callout:none] focus-visible:ring-3 focus-visible:ring-ring/50"
        >
          <span
            aria-hidden="true"
            className="absolute inset-y-0 left-0 bg-primary"
            style={{ width: `${commit.progress * 100}%` }}
          />
          <span
            className={cn(
              "relative font-semibold transition-colors motion-reduce:transition-none",
              commit.progress >= 0.5 ? "text-primary-foreground" : "text-foreground",
            )}
          >
            Press and hold to commit
          </span>
        </button>
        {/* Space reserved (min-h): the hint appearing must not shift the
            button out from under a finger that is about to hold again. */}
        <p aria-live="polite" className="min-h-10 text-center text-sm text-muted-foreground">
          {commit.releasedEarly
            ? "Hold it for a second. This one is meant to take a moment."
            : null}
        </p>
        {/* Untimed alternative (WCAG 2.1.1, and VoiceOver, whose activation
            can't hold): the hold is a moment of commitment, never a gate. */}
        <p className="text-center text-sm text-muted-foreground">
          Hold for one second, or{" "}
          <button
            type="button"
            onClick={commit.now}
            className="-my-3 inline-block py-3 font-medium text-primary underline underline-offset-4"
          >
            tap to continue
          </button>
        </p>
      </StepFooter>
    </>
  );
}

/** Pointer hold → `advance()` once after {@link HOLD_MS}; an early release
 *  resets the fill and flags the hint. rAF is cancelled on release and on
 *  unmount; a ref guard makes the commit fire at most once. */
function useHoldToCommit(advance: StepProps["advance"]) {
  const [progress, setProgress] = useState(0);
  const [releasedEarly, setReleasedEarly] = useState(false);
  const frame = useRef<number | null>(null);
  const committed = useRef(false);

  const stop = () => {
    if (frame.current !== null) cancelAnimationFrame(frame.current);
    frame.current = null;
  };

  useEffect(() => stop, []);

  const now = useCallback(() => {
    if (committed.current) return;
    committed.current = true;
    stop();
    setProgress(1);
    advance();
  }, [advance]);

  const start = useCallback(() => {
    if (committed.current) return;
    stop();
    setReleasedEarly(false);
    const startedAt = performance.now();
    const tick = (t: number) => {
      const p = Math.min((t - startedAt) / HOLD_MS, 1);
      if (p >= 1) {
        now();
        return;
      }
      setProgress(p);
      frame.current = requestAnimationFrame(tick);
    };
    frame.current = requestAnimationFrame(tick);
  }, [now]);

  const cancel = useCallback(() => {
    if (committed.current || frame.current === null) return;
    stop();
    setProgress(0);
    setReleasedEarly(true);
  }, []);

  return { progress, releasedEarly, start, cancel, now };
}
